import { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery, useTheme } from '@mui/material';

// import ArrowsLeftRightIcon from '~/assets/icons/svgs/arrows-left-right.svg?react';
import QRCodeIcon from '~/assets/icons/svgs/qr-code.svg?react';
import { ColorInputPicker } from '~/components/atoms/ColorInputPicker';
import DesignStepBlockHeaderLayout from '~/components/QRCodeDesign/DesignStepBlockHeaderLayout';
import Accordion from '~/components/ui/Accordion/Accordion';
import AsyncSwitch from '~/components/ui/AsyncSwitch';
import Select from '~/components/ui/Select';
import { Text } from '~/components/ui/text';
import { BASIC_GRADIENT_STYLES, DESKTOP_MIN_WIDTH, GRADIENT_TYPES } from '~/constants/common';
import useQrCodeStylesData from '~/hooks/useQrCodeStylesData';
import { ChangeGradientParams, QrCodeStyle } from '~/types/qrCode';

import {
  StyledBlock,
  StyledGradientForm,
  StyledListImgContainer,
  StyledShapeContainer,
  StyledShapesContainer,
  // StyledSwipeButton,
} from './styled';

type QRCodeCornerStyleProps = {
  qrCodeStyles?: QrCodeStyle;
  onChangeBorderStyle: (newBorderStyle: number) => void;
  onChangeCenterStyle: (newCenterStyle: number) => void;
  onSwitchBorderAndCenterColors: () => void;
  onChangeBorderColor: (newBorderColor: string) => void;
  onChangeCenterColor: (newCenterColor: string) => void;
  onChangeBorderGradient: (params: ChangeGradientParams) => void;
  onChangeCenterGradient: (params: ChangeGradientParams) => void;
  onChangeIsBorderGradient: (params: ChangeEvent<HTMLInputElement>) => void;
  onChangeIsCenterGradient: (event: ChangeEvent<HTMLInputElement>) => void;
  defaultExpanded?: boolean;
};

const DEFAULT_START_COLOR = '#000000';
const DEFAULT_END_COLOR = '#ffffff';

export default function QRCodeCornerStyle({
  qrCodeStyles,
  onChangeIsBorderGradient,
  onChangeIsCenterGradient,
  onChangeBorderGradient,
  onChangeCenterGradient,
  onChangeCenterColor,
  onChangeCenterStyle,
  // onSwitchBorderAndCenterColors,
  onChangeBorderColor,
  onChangeBorderStyle,
  defaultExpanded = false,
}: QRCodeCornerStyleProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_MIN_WIDTH));

  const { data: qrCodeTemplateStyles } = useQrCodeStylesData();

  const [borderStyle, setBorderStyle] = useState(qrCodeStyles?.cornerBorder?.style);
  const [centerStyle, setCenterStyle] = useState(qrCodeStyles?.cornerCenter?.style);
  const [borderColor, setBorderColor] = useState(qrCodeStyles?.cornerBorder?.color);
  const [centerColor, setCenterColor] = useState(qrCodeStyles?.cornerCenter?.color);
  const [isBorderGradient, setIsBorderGradient] = useState(typeof qrCodeStyles?.cornerBorder?.color === 'object');
  const [isCenterGradient, setIsCenterGradient] = useState(typeof qrCodeStyles?.cornerCenter?.color === 'object');

  const handleChangeBorderStyle = (newBorderStyle: number) => {
    setBorderStyle(newBorderStyle);
    onChangeBorderStyle(newBorderStyle);
  };

  const handleChangeCenterStyle = (newCenterStyle: number) => {
    setCenterStyle(newCenterStyle);
    onChangeCenterStyle(newCenterStyle);
  };

  const handleChangeIsBorderGradient = (e: ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;

    setIsBorderGradient(checked);

    if (checked) {
      setBorderColor({ ...BASIC_GRADIENT_STYLES, color: borderColor ?? DEFAULT_START_COLOR });
    } else {
      setBorderColor(borderColor?.color ?? DEFAULT_START_COLOR);
    }

    onChangeIsBorderGradient(e);
  };

  const handleChangeIsCenterGradient = (e: ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;

    setIsCenterGradient(checked);

    if (checked) {
      setCenterColor({ ...BASIC_GRADIENT_STYLES, color: centerColor ?? DEFAULT_START_COLOR });
    } else {
      setCenterColor(centerColor?.color ?? DEFAULT_START_COLOR);
    }

    onChangeIsCenterGradient(e);
  };

  // const handleSwitchColors = () => {
  //   setBorderColor(centerColor);
  //   setCenterColor(borderColor);
  //
  //   onSwitchBorderAndCenterColors();
  // };

  // const isSwipeBtnVisible = (isCenterGradient && isBorderGradient) || (!isCenterGradient && !isBorderGradient);

  return (
    <Accordion
      variant="medium"
      sx={{ maxWidth: '100%', width: '100%' }}
      defaultExpanded={defaultExpanded}
      summary={
        <DesignStepBlockHeaderLayout
          Icon={QRCodeIcon}
          title={t('builder:designStep.cornerStyle.title')}
          description={t('builder:designStep.cornerStyle.description')}
        />
      }
      details={
        <>
          <StyledBlock>
            <Text variant="subheader" sx={{ marginTop: '8px' }}>
              {t('builder:designStep.cornerStyle.cornerFrame.title')}
            </Text>
            <Text variant="input15" sx={{ marginBottom: '-8px' }}>
              {t('builder:designStep.cornerStyle.cornerFrame.cornerFrameStyle')}
            </Text>
            <StyledShapesContainer>
              {qrCodeTemplateStyles?.positionPatternBorders?.map(({ id, previewImage }) => {
                const isSelected = id === borderStyle;

                return (
                  <StyledListImgContainer key={id} onClick={() => handleChangeBorderStyle(id)} $isSelected={isSelected}>
                    <img src={previewImage} alt="border style preview" width={18} height={18} />
                  </StyledListImgContainer>
                );
              })}
            </StyledShapesContainer>
            <StyledShapeContainer>
              <Text variant="subheader">{t('builder:designStep.cornerStyle.cornerFrame.cornerFrameColor')}</Text>
              <AsyncSwitch
                label={t('thirdStep:useGradients')}
                checked={isBorderGradient}
                onChange={handleChangeIsBorderGradient}
                sx={{ width: 'fit-content' }}
              />
              {isBorderGradient && typeof borderColor === 'object' ? (
                <StyledGradientForm>
                  <Select
                    items={GRADIENT_TYPES}
                    label={t('thirdStep:gradientType')}
                    value={borderColor?.type || GRADIENT_TYPES[0]?.value}
                    onChange={(e) => {
                      setBorderColor((prevState) => ({ ...prevState, type: e.target.value }));
                      onChangeBorderGradient({ field: 'type', value: e.target.value });
                    }}
                    fullWidth
                    sx={{ flex: 1 }}
                  />
                  <ColorInputPicker
                    label={t('thirdStep:color')}
                    onChange={(value) => {
                      setBorderColor((prevState) => ({ ...prevState, startColor: value }));
                    }}
                    sx={{ flex: 1 }}
                    value={borderColor?.startColor ?? DEFAULT_START_COLOR}
                    fullWidth
                    withInternalOnChange
                    onClosePicker={(value) => {
                      onChangeBorderGradient({ field: 'start_color', value });
                    }}
                  />

                  <ColorInputPicker
                    label={t('thirdStep:color')}
                    onChange={(value) => {
                      setBorderColor((prevState) => ({ ...prevState, endColor: value }));
                    }}
                    sx={{ flex: 1 }}
                    value={borderColor?.endColor ?? DEFAULT_END_COLOR}
                    fullWidth
                    withInternalOnChange
                    onClosePicker={(value) => {
                      onChangeBorderGradient({ field: 'end_color', value });
                    }}
                  />
                </StyledGradientForm>
              ) : (
                <ColorInputPicker
                  onChange={(newBorderColor) => {
                    setBorderColor(newBorderColor);
                  }}
                  value={borderColor as string}
                  label={t('thirdStep:color')}
                  fullWidth={!isDesktop}
                  withInternalOnChange
                  onClosePicker={(newBorderColor) => {
                    onChangeBorderColor(newBorderColor);
                  }}
                />
              )}
              {/*{isSwipeBtnVisible && (*/}
              {/*  <StyledSwipeButton aria-label="swap" disableRipple onClick={handleSwitchColors}>*/}
              {/*    <ArrowsLeftRightIcon />*/}
              {/*  </StyledSwipeButton>*/}
              {/*)}*/}
            </StyledShapeContainer>
          </StyledBlock>
          <StyledBlock>
            <Text variant="input15" sx={{ marginBottom: '-8px' }}>
              {t('builder:designStep.cornerStyle.cornerDots')}
            </Text>
            <StyledShapesContainer>
              {qrCodeTemplateStyles?.positionPatternCenters.map(({ id, previewImage }) => {
                const isSelected = id === centerStyle;

                return (
                  <StyledListImgContainer key={id} onClick={() => handleChangeCenterStyle(id)} $isSelected={isSelected}>
                    <img src={previewImage} alt="center style preview image" width={18} height={18} />
                  </StyledListImgContainer>
                );
              })}
            </StyledShapesContainer>
            <StyledShapeContainer>
              <Text variant="subheader">{t('builder:designStep.cornerStyle.cornerDotsColor')}</Text>
              <AsyncSwitch
                label={t('thirdStep:useGradients')}
                checked={isCenterGradient}
                onChange={handleChangeIsCenterGradient}
                sx={{ width: 'fit-content' }}
              />
              {isCenterGradient && typeof centerColor === 'object' ? (
                <StyledGradientForm>
                  <Select
                    items={GRADIENT_TYPES}
                    label={t('thirdStep:gradientType')}
                    value={centerColor?.type || GRADIENT_TYPES[0]?.value}
                    onChange={(e) => {
                      setCenterColor((prevState) => ({ ...prevState, type: e.target.value }));
                      onChangeCenterGradient({ field: 'type', value: e.target.value });
                    }}
                    fullWidth
                    sx={{ flex: 1 }}
                  />
                  <ColorInputPicker
                    label={t('thirdStep:color')}
                    onChange={(value) => {
                      setCenterColor((prevState) => ({ ...prevState, startColor: value }));
                    }}
                    sx={{ flex: 1 }}
                    value={centerColor?.startColor ?? DEFAULT_START_COLOR}
                    fullWidth
                    withInternalOnChange
                    onClosePicker={(value) => {
                      onChangeCenterGradient({ field: 'start_color', value });
                    }}
                  />

                  <ColorInputPicker
                    label={t('thirdStep:color')}
                    onChange={(value) => {
                      setCenterColor((prevState) => ({ ...prevState, endColor: value }));
                    }}
                    sx={{ flex: 1 }}
                    value={centerColor?.endColor ?? DEFAULT_END_COLOR}
                    fullWidth
                    withInternalOnChange
                    onClosePicker={(value) => {
                      onChangeCenterGradient({ field: 'end_color', value });
                    }}
                  />
                </StyledGradientForm>
              ) : (
                <ColorInputPicker
                  onChange={(newCenterColor) => {
                    setCenterColor(newCenterColor);
                  }}
                  value={centerColor as string}
                  label={t('thirdStep:color')}
                  fullWidth={!isDesktop}
                  withInternalOnChange
                  onClosePicker={(newCenterColor) => {
                    onChangeCenterColor(newCenterColor);
                  }}
                />
              )}
            </StyledShapeContainer>
          </StyledBlock>
        </>
      }
    />
  );
}
